import { computed } from 'vue'
import { type RouteLocationRaw, useRoute } from 'vue-router'
import type { NavItem, NavigationKey } from '@klausapp/app-navigation/types'
import { session, isArturoFlagEnabled } from '@/composables/useSession'
import { useDashboardsList } from '@/composables/useDashboardsList'
import { hasAccountRole, hasWorkspaceRole } from '@/utils/roleUtils'
import useTicketRouteParams from '@/composables/useTicketRouteParams'
import { useActivitiesCount } from '@/composables/useActivitiesCount'
import { useEvent } from '@/composables/useEvent'
import { useTasksIncompleteQuery } from '@/modules/tasks/queries/useTasksIncompleteQuery'
import { useOnboardingStatusQuery } from '@/modules/hub/queries/useOnboardingStatusQuery'
import i18n from '@/i18n'
import {
  coachingSessionsCount,
  getCoachingSessionsCount,
  getPinsCount,
  pinsCount,
} from './components/useCoachingCounts'

// Constants
const HUB_ONBOARDING_BADGE_TTL_MS = 30 * 24 * 60 * 60 * 1000

const ROUTE_PREFIXES = {
  DASHBOARD: 'dashboard',
  CONVERSATIONS: 'conversations',
  TASKS: 'tasks',
  ACTIVITY: 'activity',
  COACHING: ['quizzes', 'coaching', 'pins'],
  HUB: 'hub',
  SETTINGS: ['settings', 'profile'],
  USERS_WORKSPACES: ['settings.users', 'settings.groups', 'settings.bots', 'workspace'],
} as const

export default () => {
  const route = useRoute()
  const { dashboardList, lookerReviewsDashboard, lookerReviewsAgentDashboard } = useDashboardsList()
  const { assignmentId, calibrationSessionId } = useTicketRouteParams()
  const activitiesCount = useActivitiesCount()
  const { data } = useTasksIncompleteQuery()
  const isAccountEligibleForOnboardingBadge = computed(() => {
    const createdAt = session.account?.createdAt
    if (!createdAt) return false
    const createdMs = new Date(createdAt).getTime()
    return Number.isFinite(createdMs) && Date.now() < createdMs + HUB_ONBOARDING_BADGE_TTL_MS
  })
  const { data: onboardingStatus } = useOnboardingStatusQuery({
    // HubView's own observer still loads onboarding status on the hub page.
    enabled: isAccountEligibleForOnboardingBadge,
  })

  // Initialize counts
  getPinsCount()
  getCoachingSessionsCount()

  // Setup event listeners for count refreshes
  useEvent('refresh-pins-count', () => setTimeout(getPinsCount, 1000))
  useEvent('refresh-coaching-sessions-count', () => setTimeout(getCoachingSessionsCount, 1000))

  // Computed values
  const dashboardRoute = computed(() => {
    if (!dashboardList.value.length) return { id: '', name: '' }

    const targetDashboard = hasWorkspaceRole('AGENT') ? lookerReviewsAgentDashboard.value : lookerReviewsDashboard.value

    return targetDashboard || { id: '', name: '' }
  })

  const notificationsCount = computed(() => pinsCount.value + coachingSessionsCount.value)
  const onboardingUndoneCount = computed(() => onboardingStatus.value?.undoneCount || 0)
  const onboardingBadge = computed(() =>
    isAccountEligibleForOnboardingBadge.value ? onboardingUndoneCount.value : 0,
  )
  const activitiesCountTotal = computed(() => activitiesCount.value?.total || 0)
  const tasksCount = computed(() => {
    const count = (data.value?.agentQa || 0) + (data.value?.reviewerQa || 0)
    return count
  })

  // Route visibility rules
  const routeVisibility = computed(() => {
    const selfReviews = session.workspace?.settings?.selfReviews || false
    const isAgent = hasWorkspaceRole('AGENT')

    return {
      conversations: !isAgent || selfReviews,
      tasks: !isAgent,
      hub: hasAccountRole('ADMIN') && isArturoFlagEnabled('zqa_qa_hub'),
    }
  })

  // Route generation
  const getRoute = (key: NavigationKey): RouteLocationRaw => {
    const routes: Record<NavigationKey, () => RouteLocationRaw> = {
      dashboard: () => ({ name: 'dashboard.looker', params: { id: dashboardRoute.value.id } }),

      conversations: () => ({ name: 'conversations' }),

      tasks: () => ({
        name: 'tasks',
        params: assignmentId.value
          ? { assignmentId: assignmentId.value }
          : { calibrationSessionId: calibrationSessionId.value },
      }),

      activity: () => ({ name: 'activity' }),

      coaching: () => ({ name: 'coaching.sessions' }),

      hub: () => ({ name: 'hub' }),
    }

    return routes[key]?.() || {}
  }

  // Navigation items
  const navItemsMap = computed((): NavItem[] => {
    const items: NavItem[] = [
      {
        label: i18n.t('modules.application.navbar.dashboard'),
        key: 'dashboard',
      },
    ]

    if (routeVisibility.value.conversations) {
      items.push({
        label: i18n.t('modules.application.navbar.conversations'),
        key: 'conversations',
      })
    }

    if (routeVisibility.value.tasks) {
      items.push({
        label: i18n.t('modules.application.navbar.tasks'),
        key: 'tasks',
        badge: tasksCount.value,
      })
    }

    items.push({
      label: i18n.t('modules.application.navbar.activity'),
      key: 'activity',
      badge: activitiesCountTotal.value,
    })

    items.push({
      label: i18n.t('modules.application.navbar.coaching'),
      key: 'coaching',
      badge: notificationsCount.value,
    })

    if (routeVisibility.value.hub) {
      items.push({
        label: i18n.t('modules.application.navbar.hub'),
        key: 'hub',
        badge: onboardingBadge.value,
      })
    }

    return items
  })

  // Current route detection
  const currentRouteName = computed((): string => {
    const routeName = route.name?.toString()
    if (!routeName) return ''

    if (routeName.startsWith(ROUTE_PREFIXES.DASHBOARD)) return 'dashboard'
    if (routeName.startsWith(ROUTE_PREFIXES.CONVERSATIONS)) return 'conversations'
    if (routeName.startsWith(ROUTE_PREFIXES.TASKS)) return 'tasks'
    if (routeName.startsWith(ROUTE_PREFIXES.ACTIVITY)) return 'activity'
    if (ROUTE_PREFIXES.COACHING.some((prefix) => routeName.startsWith(prefix))) return 'coaching'
    if (routeName.startsWith(ROUTE_PREFIXES.HUB)) return 'hub'
    if (ROUTE_PREFIXES.USERS_WORKSPACES.some((prefix) => routeName.startsWith(prefix))) return 'users-workspaces'
    if (ROUTE_PREFIXES.SETTINGS.some((prefix) => routeName.startsWith(prefix))) return 'settings'

    return routeName
  })

  // Maps current route prefix to a safe parent route without workspace-specific params
  const getWorkspaceSwitchRoute = (): RouteLocationRaw => {
    const routeName = route.name?.toString()
    if (!routeName) return { path: '/dashboard' }

    // Map to top-level routes without workspace-specific params
    if (routeName.startsWith(ROUTE_PREFIXES.DASHBOARD)) {
      return { name: 'dashboard.layout' }
    }
    if (routeName.startsWith(ROUTE_PREFIXES.CONVERSATIONS)) {
      return { name: 'conversations' }
    }
    if (routeName.startsWith(ROUTE_PREFIXES.TASKS)) {
      return { name: 'tasks' } // Without assignmentId/calibrationSessionId params
    }
    if (routeName.startsWith(ROUTE_PREFIXES.ACTIVITY)) {
      return { name: 'activity' }
    }
    if (ROUTE_PREFIXES.COACHING.some((prefix) => routeName.startsWith(prefix))) {
      return { name: 'coaching.sessions' }
    }
    if (routeName.startsWith(ROUTE_PREFIXES.HUB)) {
      return { name: 'hub' }
    }
    if (ROUTE_PREFIXES.SETTINGS.some((prefix) => routeName.startsWith(prefix))) {
      return { name: 'settings' } // Will redirect to profile.general
    }
    if (ROUTE_PREFIXES.USERS_WORKSPACES.some((prefix) => routeName.startsWith(prefix))) {
      return { name: 'settings' } // Safe fallback to settings
    }

    // Fallback to dashboard for unknown routes
    return { path: '/dashboard' }
  }

  return {
    getRoute,
    navItemsMap,
    currentRouteName,
    getWorkspaceSwitchRoute,
  }
}
